/**
 * Drug interaction checker.
 * Uses localStorage-backed interaction data from db.ts.
 * Also uses RvLite's get() for drug metadata (food interactions).
 * Zero network calls -- everything runs on-device.
 */

import type { DrugInteraction } from '../types/index';
import { getDb, findInteraction, getDrugInteractions, getDrugFromCache } from './db';

// ---------------------------------------------------------------------------
// Severity helpers
// ---------------------------------------------------------------------------

type InteractionSeverity = DrugInteraction['severity'];

const VALID_SEVERITIES = new Set<InteractionSeverity>([
  'minor', 'moderate', 'major', 'contraindicated',
]);

function parseSeverity(raw: unknown): InteractionSeverity {
  const s = String(raw ?? '').toLowerCase() as InteractionSeverity;
  return VALID_SEVERITIES.has(s) ? s : 'moderate';
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Check whether two specific drugs have a known interaction.
 */
export async function checkInteraction(
  drugA: string,
  drugB: string,
): Promise<DrugInteraction | null> {
  const ix = findInteraction(drugA, drugB);
  if (!ix) return null;

  return {
    drug_a: drugA,
    drug_b: drugB,
    severity: parseSeverity(ix.severity),
    description: ix.description,
    clinical_effect: ix.clinical_effect,
  };
}

/**
 * Check every unique pair of the provided drug IDs for interactions.
 */
export async function checkAllInteractions(
  drugIds: string[],
): Promise<DrugInteraction[]> {
  if (drugIds.length < 2) return [];

  const interactions: DrugInteraction[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < drugIds.length; i++) {
    for (let j = i + 1; j < drugIds.length; j++) {
      const key = [drugIds[i], drugIds[j]].sort().join('|');
      if (seen.has(key)) continue;
      seen.add(key);

      const result = await checkInteraction(drugIds[i], drugIds[j]);
      if (result) interactions.push(result);
    }
  }

  return interactions;
}

/**
 * Check whether a drug has a known food interaction.
 * Uses RvLite's native get() to retrieve drug metadata.
 */
export async function checkFoodInteraction(
  drugId: string,
  food: string,
): Promise<string | null> {
  // Try localStorage cache first
  const cached = getDrugFromCache(drugId);
  if (cached) {
    const raw = cached.food_interactions;
    if (raw == null || raw === '') return null;
    let interactions: string[];
    if (Array.isArray(raw)) {
      interactions = raw.map(String);
    } else if (typeof raw === 'string') {
      try { interactions = JSON.parse(raw); } catch { interactions = [String(raw)]; }
    } else {
      return null;
    }
    const lowerFood = food.toLowerCase();
    const match = interactions.find((i) => i.toLowerCase().includes(lowerFood));
    return match ?? null;
  }

  const db = await getDb();
  if (!db) return null;

  try {
    const result = db.get(drugId);
    if (!result) return null;

    const meta = (result as Record<string, unknown>).metadata ?? result;
    const raw = (meta as Record<string, unknown>).food_interactions;
    if (raw == null || raw === '') return null;

    let interactions: string[];
    if (Array.isArray(raw)) {
      interactions = raw.map(String);
    } else if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        interactions = Array.isArray(parsed) ? parsed : [String(parsed)];
      } catch {
        interactions = raw.split(',').map((s) => s.trim()).filter(Boolean);
      }
    } else {
      return null;
    }

    const needle = food.toLowerCase();
    return interactions.find((entry) => entry.toLowerCase().includes(needle)) ?? null;
  } catch {
    return null;
  }
}

/**
 * Insert or update a drug interaction (stored in localStorage).
 */
export async function upsertInteraction(
  interaction: DrugInteraction,
): Promise<void> {
  const allInteractions = getDrugInteractions();

  // Remove existing pair
  const filtered = allInteractions.filter(
    (ix) => !(
      (ix.drug_a_id === interaction.drug_a && ix.drug_b_id === interaction.drug_b) ||
      (ix.drug_a_id === interaction.drug_b && ix.drug_b_id === interaction.drug_a)
    ),
  );

  filtered.push({
    id: `${interaction.drug_a}_${interaction.drug_b}`,
    drug_a_id: interaction.drug_a,
    drug_b_id: interaction.drug_b,
    severity: interaction.severity,
    description: interaction.description,
    clinical_effect: interaction.clinical_effect,
  });

  localStorage.setItem('aftercare:drug_interactions', JSON.stringify(filtered));
}
